import { useCallback, useEffect, useMemo, useState } from 'react';
import { RefreshCw, AlertCircle, CheckCircle, Search, Building2 } from 'lucide-react';
import { useApi } from '../contexts/ApiContext';
import { ApiResponse, SyncLog } from '../types';
import { logoUrlFor } from '../lib/registrar-logos';
import { runSync, formatSyncOutcome } from '../lib/sync';
import { format } from 'date-fns';
import PageHeader from '../components/ui/PageHeader';
import EmptyState from '../components/ui/EmptyState';
import { SkeletonCard } from '../components/ui/Skeleton';

/**
 * Sync-only view of the Registrars page, shown to users who hold the
 * can_trigger_sync grant but are not admins.
 *
 * Deliberately minimal: it reads /accounts/sync-targets (no credential
 * metadata, no 1Password references) and can do exactly one thing — refresh an
 * account. Everything the admin page offers around credentials, connection
 * tests and account setup is absent here, and the corresponding endpoints stay
 * admin-only server-side.
 */
interface SyncTarget {
  id: string;
  label: string;
  registrar_type: string;
  registrar_name: string;
}

export default function RegistrarSyncPage() {
  const { fetchApi } = useApi();
  const [targets, setTargets] = useState<SyncTarget[]>([]);
  const [syncStatus, setSyncStatus] = useState<Record<string, SyncLog>>({});
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState<string | null>(null);
  const [filterText, setFilterText] = useState('');
  const [result, setResult] = useState<{ id: string; ok: boolean; message: string } | null>(null);
  const [elapsed, setElapsed] = useState(0);

  const load = useCallback(async () => {
    try {
      const [targetsRes, statusRes] = await Promise.all([
        fetchApi<ApiResponse<SyncTarget[]>>('/accounts/sync-targets'),
        fetchApi<ApiResponse<Record<string, SyncLog>>>('/accounts/sync-status').catch(() => ({ data: {} })),
      ]);
      setTargets(targetsRes.data || []);
      setSyncStatus(statusRes.data || {});
    } finally {
      setLoading(false);
    }
  }, [fetchApi]);

  useEffect(() => { load(); }, [load]);

  const filtered = useMemo(() => {
    const q = filterText.trim().toLowerCase();
    if (!q) return targets;
    return targets.filter(
      (t) => t.label.toLowerCase().includes(q) || t.registrar_name.toLowerCase().includes(q)
    );
  }, [targets, filterText]);

  const handleSync = async (target: SyncTarget) => {
    setSyncing(target.id);
    setResult(null);
    setElapsed(0);
    try {
      // Polls in the background; a full sync can take a couple of minutes.
      const outcome = await runSync(fetchApi, target.id, setElapsed);
      setResult({ id: target.id, ok: outcome.status === 'completed', message: formatSyncOutcome(outcome) });
      await load();
    } catch (error) {
      setResult({
        id: target.id,
        ok: false,
        message: error instanceof Error ? error.message : 'Sync failed',
      });
    } finally {
      setSyncing(null);
      setElapsed(0);
    }
  };

  if (loading) {
    return (
      <div className="p-6 space-y-4">
        <PageHeader title="Registrar Sync" />
        <SkeletonCard lines={5} />
      </div>
    );
  }

  return (
    <div className="p-6 space-y-4">
      <PageHeader
        title="Registrar Sync"
        description="Pull the latest domain data from a registrar. Credentials and registrar settings are managed by an admin."
      />

      {targets.length > 3 && (
        <div className="relative max-w-xs">
          <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
          <input
            value={filterText}
            onChange={(e) => setFilterText(e.target.value)}
            placeholder="Filter registrars…"
            className="input pl-8 text-sm"
          />
        </div>
      )}

      {filtered.length === 0 ? (
        <div className="border border-slate-200 rounded-lg bg-surface">
          <EmptyState
            compact
            icon={Building2}
            title="No registrar accounts are available to sync."
          />
        </div>
      ) : (
        <div className="border border-slate-200 rounded-lg divide-y divide-slate-200 bg-surface">
          {filtered.map((target) => {
            const last = syncStatus[target.id];
            const isSyncing = syncing === target.id;
            const logo = logoUrlFor(target.registrar_type);
            return (
              <div key={target.id} className="flex items-center gap-3 p-4">
                {logo && <img src={logo} alt="" className="h-6 w-6 rounded object-contain shrink-0" />}
                <div className="min-w-0 flex-1">
                  <div className="text-sm font-medium text-slate-900 truncate">
                    {target.registrar_name}
                    <span className="text-slate-400 font-normal"> · {target.label}</span>
                  </div>
                  <div className="text-xs text-slate-500 mt-0.5">
                    {last?.completed_at
                      ? `Last synced ${format(new Date(last.completed_at), 'd MMM yyyy HH:mm')}${
                          last.domains_synced ? ` — ${last.domains_synced} domains` : ''
                        }`
                      : 'Never synced'}
                  </div>
                  {result?.id === target.id && (
                    <div
                      className={`flex items-start gap-1.5 text-xs mt-1.5 ${
                        result.ok ? 'text-emerald-700' : 'text-red-600'
                      }`}
                    >
                      {result.ok ? (
                        <CheckCircle className="h-3.5 w-3.5 mt-px shrink-0" />
                      ) : (
                        <AlertCircle className="h-3.5 w-3.5 mt-px shrink-0" />
                      )}
                      <span>{result.message}</span>
                    </div>
                  )}
                </div>
                <button
                  onClick={() => handleSync(target)}
                  disabled={!!syncing}
                  className="btn btn-secondary text-sm shrink-0 disabled:opacity-50"
                >
                  <RefreshCw className={`h-4 w-4 mr-1.5 ${isSyncing ? 'animate-spin' : ''}`} />
                  {isSyncing ? (elapsed > 3 ? `Syncing… ${elapsed}s` : 'Syncing…') : 'Sync'}
                </button>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
